import React from 'react';
import { toast } from 'react-hot-toast';
import useEventStore from '../stores/eventStore';
import { aggregateCrowd, normalizeStoredPreference } from './aggregate';
import {
  publishVibeSnapshot,
  saveVibePreference,
  watchCrowdVibe,
  watchOwnVibePreference,
} from './partyVibe';
import { preferenceToDraft } from './selection';
import {
  blocksPartyUntilVibe,
  canSeeCrowdVibe,
  createVibeSession,
  vibeSessionReducer,
} from './vibeSession';

/**
 * Live party vibe controller.
 * Draft changes stay in component state until Save / Update My Vibe.
 */
export default function usePartyVibe({
  eventId,
  userId,
  isHost,
  enabled,
}) {
  const participants = useEventStore((state) => state.eventData?.participants || []);
  const presenceByUid = useEventStore((state) => state.presenceByUid || {});
  const [session, dispatch] = React.useReducer(
    vibeSessionReducer,
    isHost,
    createVibeSession,
  );
  const [draft, setDraft] = React.useState(() => preferenceToDraft(null));
  const [preferencesByUid, setPreferencesByUid] = React.useState({});
  const [vibeState, setVibeState] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [updating, setUpdating] = React.useState(false);
  const draftDirtyRef = React.useRef(false);

  React.useEffect(() => {
    dispatch({ type: 'reset', isHost });
    setDraft(preferenceToDraft(null));
    setPreferencesByUid({});
    setVibeState(null);
    draftDirtyRef.current = false;
  }, [eventId, userId, isHost]);

  React.useEffect(() => {
    if (!enabled || !eventId || !userId) return undefined;
    return watchOwnVibePreference(eventId, userId, (raw) => {
      const preference = raw ? normalizeStoredPreference(raw) : null;
      dispatch({ type: 'own-loaded', isHost, preference });
      if (!draftDirtyRef.current) {
        setDraft(preferenceToDraft(preference));
      }
    });
  }, [enabled, eventId, userId, isHost]);

  const hasSubmitted = Boolean(session.saved);
  const crowdPreferences = React.useMemo(() => {
    if (!userId || !session.saved) return preferencesByUid;
    return { ...preferencesByUid, [userId]: session.saved };
  }, [preferencesByUid, session.saved, userId]);
  const canWatchCrowd = Boolean(
    enabled
    && eventId
    && canSeeCrowdVibe({ isHost, hasSubmitted }),
  );

  React.useEffect(() => {
    if (!canWatchCrowd) return undefined;
    return watchCrowdVibe(eventId, (update) => {
      if (update.preferencesByUid) setPreferencesByUid(update.preferencesByUid);
      if (Object.prototype.hasOwnProperty.call(update, 'vibeState')) {
        setVibeState(update.vibeState);
      }
    });
  }, [canWatchCrowd, eventId]);

  const crowd = React.useMemo(
    () => aggregateCrowd({
      participants,
      presenceByUid,
      preferencesByUid: crowdPreferences,
    }),
    [participants, presenceByUid, crowdPreferences],
  );

  const updateDraft = React.useCallback((next) => {
    draftDirtyRef.current = session.phase === 'editing' || session.phase === 'choose';
    setDraft(next);
  }, [session.phase]);

  const beginEdit = React.useCallback(() => {
    if (!session.saved) return;
    draftDirtyRef.current = true;
    setDraft(preferenceToDraft(session.saved));
    dispatch({ type: 'edit' });
  }, [session.saved]);

  const cancelEdit = React.useCallback(() => {
    draftDirtyRef.current = false;
    setDraft(preferenceToDraft(session.saved));
    dispatch({ type: 'cancel' });
  }, [session.saved]);

  const save = React.useCallback(async () => {
    if (!eventId || !userId || saving) return null;
    setSaving(true);
    try {
      const preference = await saveVibePreference(eventId, userId, draft, session.saved);
      draftDirtyRef.current = false;
      setDraft(preferenceToDraft(preference));
      dispatch({ type: 'saved', preference });
      return preference;
    } catch (error) {
      if (error?.code === 'missing-feel') {
        toast.error('Choose how it should feel');
      } else {
        toast.error('Could not save your vibe');
      }
      return null;
    } finally {
      setSaving(false);
    }
  }, [draft, eventId, saving, session.saved, userId]);

  const updateVibe = React.useCallback(async () => {
    if (!isHost || !eventId || !userId || updating) return null;
    setUpdating(true);
    try {
      const result = await publishVibeSnapshot({
        eventId,
        hostUid: userId,
        participants,
        presenceByUid,
        preferencesByUid: crowdPreferences,
      });
      dispatch({ type: 'notice', notice: 'Vibe updated' });
      return result;
    } catch (error) {
      toast.error('Could not update the vibe');
      return null;
    } finally {
      setUpdating(false);
    }
  }, [crowdPreferences, eventId, isHost, participants, presenceByUid, updating, userId]);

  return {
    phase: session.phase,
    saved: session.saved,
    draft,
    notice: session.notice,
    saving,
    updating,
    vibeState,
    crowd,
    preferencesByUid,
    showCrowd: canSeeCrowdVibe({
      isHost,
      hasSubmitted,
    }),
    blocksParty: enabled && blocksPartyUntilVibe({ isHost, phase: session.phase }),
    updateDraft,
    beginEdit,
    cancelEdit,
    save,
    updateVibe,
  };
}
